"use client";

import { useEnv } from "@faire/web--source/common/env/client";
import { getGlobalProperty } from "@faire/web--source/common/globals/getGlobalProperty";
import { HistoryTiming } from "@faire/web--source/common/performance/HistoryTiming";
import { reloadExpiredSessions } from "@faire/web--source/common/releaseReloader";
import { trackRetailersReactAppInitView } from "@faire/web-api--source/events/retailers/view/reactAppInit";
import { trackSessionsLocalusertimeView } from "@faire/web-api--source/events/sessions/view/localusertime";
import { History } from "history";
import once from "lodash/once";
import React, { lazy, Suspense, useLayoutEffect } from "react";

const initializeApp = once((history: History, appName: string = "unknown") => {
  const recordLifecycleEvents = getGlobalProperty(
    "recordLifecycleEvents",
    false
  );
  if (recordLifecycleEvents) {
    const htmlSource = "nextjs";
    trackRetailersReactAppInitView(
      htmlSource,
      getGlobalProperty("routeProductArea", ""),
      getGlobalProperty("templatedPath", ""),
      appName
    );
  }

  reloadExpiredSessions();

  HistoryTiming.get().initialize(history);

  trackSessionsLocalusertimeView(`${new Date().valueOf()}`);
});

/**
 * Executes the necessary steps of src/main.tsx and src/initializeApp.ts
 * on the client of the nextjs app.
 */
const useAppInitialization = (history: History) => {
  const { appName } = useEnv();

  useLayoutEffect(() => {
    initializeApp(history, appName);
  }, [history, appName]);
};

const LazyPostInteractiveInitializer = lazy(() =>
  import(
    /* webpackChunkName: "post-app-initializer" */ "@faire/signup/app/_lib/initialization/PostInteractiveInitializer"
  ).then((m) => ({
    default: m.PostInteractiveInitializer,
  }))
);

export const AppInitializer: React.FC<{ history: History }> = ({ history }) => {
  useAppInitialization(history);

  return (
    <Suspense fallback={null}>
      <LazyPostInteractiveInitializer history={history} />
    </Suspense>
  );
};
